export type YesNo = 'sim' | 'nao' | '';

export type HowEntered = 'turismo_isencao_visto' | 'visto' | 'outra' | '';
export type HadResidencyBefore = 'nunca' | 'sim_valida' | 'sim_caducada' | 'sim_cancelada' | '';
export type StudiedInPortugal = 'nao' | 'estudando' | 'concluido' | '';
export type StudyLevel = 'secundario' | 'profissional' | 'licenciatura' | 'mestrado' | 'doutoramento' | 'outro' | '';

export interface ResidenceResponses {
  personal: {
    fullName: string;
    nationality: string;
    age: string;
  };
  currentSituation: {
    inPortugal: YesNo;
    since: string;
    howEntered: HowEntered;
    howEnteredOther: string;
    hadResidencyBefore: HadResidencyBefore;
  };
  workStudy: {
    currentlyWorking: YesNo;
    hasContractOrActivity: YesNo;
    studiedInPortugal: StudiedInPortugal;
    studyLevel: StudyLevel;
    studyLevelOther: string;
  };
  family: {
    hasChildren: YesNo;
    childUnder18: YesNo;
    childInPortugal: YesNo;
    childPortugueseNationality: YesNo;
    hasCloseRelativeWithStatus: YesNo;
    relationshipDegree: string;
  };
  specialSituations: {
    bornInPortugal: YesNo;
    hasHealthCondition: YesNo;
    hadResidencyExpiredAndStayed: YesNo;
    wasVictimOfExploitation: YesNo;
    hadStudentOrHighlyQualifiedResidency: YesNo;
    hadInternationalProtection: YesNo;
    hasInvestmentOrCompany: YesNo;
  };
  migratoryHistory: {
    hadRequestDenied: YesNo;
    hadDeportationOrExpulsionOrder: YesNo;
    wasEverIrregular: YesNo;
  };
  documents: {
    items: string[];
    otherDescription: string;
  };
  description: {
    situationDescription: string;
  };
}

export const emptyResponses = (): ResidenceResponses => ({
  personal: {
    fullName: '',
    nationality: '',
    age: ''
  },
  currentSituation: {
    inPortugal: '',
    since: '',
    howEntered: '',
    howEnteredOther: '',
    hadResidencyBefore: ''
  },
  workStudy: {
    currentlyWorking: '',
    hasContractOrActivity: '',
    studiedInPortugal: '',
    studyLevel: '',
    studyLevelOther: ''
  },
  family: {
    hasChildren: '',
    childUnder18: '',
    childInPortugal: '',
    childPortugueseNationality: '',
    hasCloseRelativeWithStatus: '',
    relationshipDegree: ''
  },
  specialSituations: {
    bornInPortugal: '',
    hasHealthCondition: '',
    hadResidencyExpiredAndStayed: '',
    wasVictimOfExploitation: '',
    hadStudentOrHighlyQualifiedResidency: '',
    hadInternationalProtection: '',
    hasInvestmentOrCompany: ''
  },
  migratoryHistory: {
    hadRequestDenied: '',
    hadDeportationOrExpulsionOrder: '',
    wasEverIrregular: ''
  },
  documents: {
    items: [],
    otherDescription: ''
  },
  description: {
    situationDescription: ''
  }
});

export const DOCUMENT_VALUES = [
  'passaporte',
  'nif',
  'niss',
  'comprovativoMorada',
  'contratoTrabalho',
  'recibosVencimento',
  'documentosEscolares',
  'documentosFilhos',
  'documentosResidenciaAnterior',
  'documentosMedicos',
  'outros'
] as const;

export type WizardStepId =
  | 'personal'
  | 'situation'
  | 'workStudy'
  | 'family'
  | 'special'
  | 'history'
  | 'documents'
  | 'description'
  | 'review';

export const WIZARD_STEPS: WizardStepId[] = [
  'personal',
  'situation',
  'workStudy',
  'family',
  'special',
  'history',
  'documents',
  'description',
  'review'
];

export type ReviewLine = { label: string; value: string };
export type ReviewSection = { title: string; lines: ReviewLine[] };

type ResidenceFormT = (key: string) => string;

function line(label: string, value: string | undefined | null): ReviewLine | null {
  const v = value?.toString().trim();
  if (!v) return null;
  return { label, value: v };
}

function lines(...items: (ReviewLine | null)[]): ReviewLine[] {
  return items.filter((x): x is ReviewLine => x !== null);
}

export function buildReviewSummary(data: ResidenceResponses, t: ResidenceFormT): ReviewSection[] {
  const L = (key: string) => t(`residenceForm.reviewLabels.${key}`);
  const stepTitle = (step: WizardStepId) => t(`residenceForm.steps.${step}`);
  const yn = (v: YesNo) => (v === 'sim' ? t('common.yes') : v === 'nao' ? t('common.no') : '');
  const howEntered = (v: HowEntered) => (v ? t(`residenceForm.howEntered.${v}`) : '');
  const hadResidencyBefore = (v: HadResidencyBefore) => (v ? t(`residenceForm.hadResidencyBefore.${v}`) : '');
  const studiedInPortugal = (v: StudiedInPortugal) => (v ? t(`residenceForm.studiedInPortugal.${v}`) : '');
  const studyLevel = (v: StudyLevel) => (v ? t(`residenceForm.studyLevel.${v}`) : '');
  const documentLabel = (v: string) => t(`residenceForm.documentsList.${v}`);

  const sections: ReviewSection[] = [];

  sections.push({
    title: stepTitle('personal'),
    lines: lines(
      line(L('fullName'), data.personal.fullName),
      line(L('nationality'), data.personal.nationality),
      line(L('age'), data.personal.age)
    )
  });

  const situationLines = lines(
    line(L('inPortugal'), yn(data.currentSituation.inPortugal)),
    line(L('hadResidencyBefore'), hadResidencyBefore(data.currentSituation.hadResidencyBefore))
  );
  if (data.currentSituation.inPortugal === 'sim') {
    situationLines.push(
      ...lines(
        line(L('since'), data.currentSituation.since),
        line(
          L('howEntered'),
          data.currentSituation.howEntered === 'outra'
            ? data.currentSituation.howEnteredOther
            : howEntered(data.currentSituation.howEntered)
        )
      )
    );
  }
  sections.push({ title: stepTitle('situation'), lines: situationLines });

  sections.push({
    title: stepTitle('workStudy'),
    lines: lines(
      line(L('currentlyWorking'), yn(data.workStudy.currentlyWorking)),
      line(L('hasContractOrActivity'), yn(data.workStudy.hasContractOrActivity)),
      line(L('studiedInPortugal'), studiedInPortugal(data.workStudy.studiedInPortugal)),
      data.workStudy.studiedInPortugal && data.workStudy.studiedInPortugal !== 'nao'
        ? line(
            L('studyLevel'),
            data.workStudy.studyLevel === 'outro' ? data.workStudy.studyLevelOther : studyLevel(data.workStudy.studyLevel)
          )
        : null
    )
  });

  sections.push({
    title: stepTitle('family'),
    lines: lines(
      line(L('hasChildren'), yn(data.family.hasChildren)),
      data.family.hasChildren === 'sim' ? line(L('childUnder18'), yn(data.family.childUnder18)) : null,
      data.family.hasChildren === 'sim' ? line(L('childInPortugal'), yn(data.family.childInPortugal)) : null,
      data.family.hasChildren === 'sim'
        ? line(L('childPortugueseNationality'), yn(data.family.childPortugueseNationality))
        : null,
      line(L('hasCloseRelativeWithStatus'), yn(data.family.hasCloseRelativeWithStatus)),
      data.family.hasCloseRelativeWithStatus === 'sim'
        ? line(L('relationshipDegree'), data.family.relationshipDegree)
        : null
    )
  });

  sections.push({
    title: stepTitle('special'),
    lines: lines(
      line(L('bornInPortugal'), yn(data.specialSituations.bornInPortugal)),
      line(L('hasHealthCondition'), yn(data.specialSituations.hasHealthCondition)),
      line(L('hadResidencyExpiredAndStayed'), yn(data.specialSituations.hadResidencyExpiredAndStayed)),
      line(L('wasVictimOfExploitation'), yn(data.specialSituations.wasVictimOfExploitation)),
      line(
        L('hadStudentOrHighlyQualifiedResidency'),
        yn(data.specialSituations.hadStudentOrHighlyQualifiedResidency)
      ),
      line(L('hadInternationalProtection'), yn(data.specialSituations.hadInternationalProtection)),
      line(L('hasInvestmentOrCompany'), yn(data.specialSituations.hasInvestmentOrCompany))
    )
  });

  sections.push({
    title: stepTitle('history'),
    lines: lines(
      line(L('hadRequestDenied'), yn(data.migratoryHistory.hadRequestDenied)),
      line(L('hadDeportationOrExpulsionOrder'), yn(data.migratoryHistory.hadDeportationOrExpulsionOrder)),
      line(L('wasEverIrregular'), yn(data.migratoryHistory.wasEverIrregular))
    )
  });

  sections.push({
    title: stepTitle('documents'),
    lines: lines(
      data.documents.items.length
        ? line(L('documentsItems'), data.documents.items.map((d) => documentLabel(d)).join(', '))
        : null,
      data.documents.items.includes('outros')
        ? line(L('documentsOther'), data.documents.otherDescription)
        : null
    )
  });

  sections.push({
    title: stepTitle('description'),
    lines: lines(line(L('situationDescription'), data.description.situationDescription))
  });

  return sections.filter((s) => s.lines.length > 0);
}
